/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import {
  Grid,
  Autocomplete,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { getData } from "./Services/getData";
import { postData } from "./Services/postData";
import { toast } from "react-toastify";
import { urlEnum } from "./Utils/UrlEnum";
import { CustomerModel } from "./Utils/Models";
import { Vocabulary } from "./Utils/Vocabulary";
import { updateData } from "./Services/updateData";

type CountryType = {
  name: string;
  states: { name: string }[];
};

export default function MainUser() {
  const [user, setUser] = useState<CustomerModel>(new CustomerModel());
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [countyOptions, setCountyOptions] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [countryStatesMap, setCountryStatesMap] = useState<
    Record<string, string[]>
  >({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser((prev: CustomerModel) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field: string, value: string | null) => {
    const newValue = value || "";

    setUser((prev: CustomerModel) => {
      const updated = { ...prev };

      if (updated) {
        if (field === "country") {
          updated.address.country = newValue;
          updated.address.county = "";
          updated.address.city = "";
          setCountyOptions(countryStatesMap[newValue] || []);
          setCityOptions([]);
        } else if (field === "county") {
          updated.address.county = newValue;
          updated.address.city = "";
          setCityOptions([]);
        } else if (field === "city") {
          updated.address.city = newValue;
        }
      }

      return updated;
    });
  };

  // Fetch country and states
  useEffect(() => {
    getData("https://countriesnow.space/api/v0.1/countries/states")
      .then((res) => {
        const countries: CountryType[] = res.data.data;

        const uniqueCountries = Array.from(
          new Map(countries.map((c) => [c.name, c])).values()
        );

        const countryNames = uniqueCountries.map((c) => c.name);

        const map: Record<string, string[]> = {};
        uniqueCountries.forEach((c) => {
          map[c.name] = c.states.map((s) => s.name);
        });

        setCountryOptions(countryNames);
        setCountryStatesMap(map);
      })
      .catch(() => toast.error("Cannot get countries"));
  }, []);

  // Fetch user data
  useEffect(() => {
    getData(`${urlEnum.customers}/users/mainUser`)
      .then((res) => {
        const fetched = res.data;
        if(fetched._id){
          setUser(fetched);
        }
      })
      .catch(() => setUser(new CustomerModel()));
  }, []);

  // Fetch cities when county changes
  useEffect(() => {
    if (user && user.address && user.address.country && user.address.county) {
      postData(`https://countriesnow.space/api/v0.1/countries/state/cities`, {
        country: user.address.country,
        state: user.address.county,
      })
        .then((res) => {
          setCityOptions(res.data.data);
        })
        .catch(() => toast.error("Cannot get cities"));
    }
  }, [user.address.country, user.address.county]);

  const handleSubmit = async () => {
    try {
      if (user._id) {
        await updateData(`${urlEnum.customers}/${user._id}`, user);
      } else {
        await postData(`${urlEnum.customers}`, user);
      }
      localStorage.setItem("mainUser", JSON.stringify(user));
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate>
      <Grid container spacing={2} style={{ marginTop: 50 }}>
        <Grid item xs={12} sm={6}>
          <TextValidator
            label="Company Name"
            name="companyName"
            value={user.companyName}
            onChange={handleChange}
            fullWidth
            validators={["required"]}
            errorMessages={["Company name is required"]}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextValidator
            label="Phone"
            name="phone"
            value={user.phone}
            onChange={handleChange}
            fullWidth
            validators={["required"]}
            errorMessages={["Phone is required"]}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextValidator
            label="Email"
            name="email"
            value={user.email}
            onChange={handleChange}
            fullWidth
            validators={["required", "isEmail"]}
            errorMessages={["Email is required", "Email is not valid"]}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextValidator
            label="Fiscal Code"
            name="fiscalCode"
            value={user.fiscalCode}
            onChange={handleChange}
            fullWidth
            validators={["required"]}
            errorMessages={["Fiscal Code is required"]}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextValidator
            label="Trade Register No"
            name="tradeRegisterNo"
            value={user.tradeRegisterNo ?? ""}
            onChange={handleChange}
            fullWidth
            validators={["required"]}
            errorMessages={["Trade Register No is required"]}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={countryOptions}
            value={user.address.country || ""}
            onChange={(e, val) => handleSelectChange("country", val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                placeholder="Select country"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={countyOptions}
            value={user.address.county}
            onChange={(e, val) => handleSelectChange("county", val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="County"
                placeholder="Select county"
              />
            )}
            disabled={!user.address.country}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={cityOptions}
            value={user.address.city}
            onChange={(e, val) => handleSelectChange("city", val)}
            renderInput={(params) => (
              <TextField {...params} label="City" placeholder="Select city" />
            )}
            disabled={!user.address.county}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextValidator
            name="commercialMarkup"
            label={Vocabulary.commercialmarkup}
            value={user.commercialMarkup ?? ""}
            onChange={handleChange}
            type="number"
            validators={["required", "isNumber"]}
            errorMessages={["This field is required", "Number only"]}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextValidator
            name="cif"
            label={Vocabulary.CIF}
            value={user.cif ?? ""}
            onChange={handleChange}
            type="number"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Grid>
      </Grid>
    </ValidatorForm>
  );
}
